import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';

export default function ICalManagement() {
  const [sources, setSources] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ unit_id: '', platform: 'airbnb', url: '' });

  const fetchData = async () => {
    try {
      const [s, u] = await Promise.all([api.get('/ical-sources'), api.get('/units')]);
      setSources(s.data); setUnits(u.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post('/ical-sources', form);
    setDialogOpen(false); setForm({ unit_id: '', platform: 'airbnb', url: '' }); fetchData();
  };

  const handleSync = async (id) => {
    setSyncing(p => ({ ...p, [id]: true }));
    try { await api.post(`/ical-sources/${id}/sync`); fetchData(); } catch (e) { console.error(e); }
    finally { setSyncing(p => ({ ...p, [id]: false })); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Delete iCal source?')) return; await api.delete(`/ical-sources/${id}`); fetchData(); };

  const statusIcon = (s) => {
    if (s === 'success') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (s === 'error') return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="ical-page">
      <div className="page-header">
        <h1 className="page-title">iCal Sync</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-ical-btn"><Plus className="w-4 h-4 mr-2" />Add Source</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add iCal Source</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Unit</Label>
                <Select value={form.unit_id} onValueChange={v => setForm({ ...form, unit_id: v })}>
                  <SelectTrigger data-testid="ical-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm({ ...form, platform: v })}>
                  <SelectTrigger data-testid="ical-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="booking">Booking.com</SelectItem>
                    <SelectItem value="vrbo">VRBO</SelectItem>
                    <SelectItem value="google">Google Calendar</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>iCal URL</Label><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://..." required data-testid="ical-url-input" /></div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="ical-submit-btn">Add Source</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="table-wrap">
        <table className="w-full data-table min-w-[600px]">
          <thead><tr><th>Unit</th><th>Platform</th><th>Status</th><th>Events</th><th>Last Sync</th><th>Actions</th></tr></thead>
          <tbody>
            {sources.map(s => (
              <tr key={s.id} data-testid={`ical-row-${s.id}`}>
                <td className="font-medium text-slate-900">{s.unit_name}</td>
                <td><Badge variant="outline" className="capitalize">{s.platform}</Badge></td>
                <td><div className="flex items-center gap-2">{statusIcon(s.sync_status)}<span className="text-sm capitalize">{s.sync_status}</span></div></td>
                <td>{s.events_count || 0}</td>
                <td className="text-sm">{s.last_sync ? format(new Date(s.last_sync), 'MMM dd HH:mm') : 'Never'}</td>
                <td>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleSync(s.id)} disabled={syncing[s.id]} data-testid={`sync-ical-${s.id}`}>
                      <RefreshCw className={`w-3.5 h-3.5 ${syncing[s.id] ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(s.id)} data-testid={`delete-ical-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {sources.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No iCal sources</td></tr>}
          </tbody>
        </table>
      </div>
      {sources.some(s => s.last_error) && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-800 mb-2">Sync Errors</h4>
          {sources.filter(s => s.last_error).map(s => (
            <p key={s.id} className="text-xs text-red-600 mb-1"><strong>{s.unit_name}:</strong> {s.last_error}</p>
          ))}
        </div>
      )}
    </div>
  );
}
